import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  KeyIcon,
  CalendarIcon,
  IdentificationIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { UserCircleIcon as UserCircleIconSolid } from '@heroicons/react/24/solid';

const ProfiloPage = () => {
  const { user, logout } = useOutletContext();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Stati per i dati del profilo
  const [profileData, setProfileData] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    username: ''
  });

  // Stati per il cambio password
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Caricamento dati utente
  useEffect(() => {
    if (user) {
      setProfileData({
        nome: user.nome || '',
        cognome: user.cognome || '',
        email: user.email || '',
        telefono: user.telefono || '',
        username: user.username || ''
      });
    }
  }, [user]);

  // Gestione modifica profilo
  const handleProfileEdit = () => {
    setIsEditingProfile(true);
  };

  const handleProfileCancel = () => {
    // Ripristina i dati originali
    setProfileData({
      nome: user.nome || '',
      cognome: user.cognome || '',
      email: user.email || '',
      telefono: user.telefono || '',
      username: user.username || ''
    });
    setIsEditingProfile(false);
  };

  const handleProfileSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/profilo', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        // Aggiorna i dati utente nel localStorage
        localStorage.setItem('user', JSON.stringify(updatedUser));
        toast.success('Profilo aggiornato con successo!');
        setIsEditingProfile(false);
        // Potresti voler aggiornare anche il contesto dell'app
        window.location.reload(); // Soluzione temporanea
      } else {
        const error = await response.json();
        toast.error(error.message || 'Errore nell\'aggiornamento del profilo');
      }
    } catch (error) {
      console.error('Errore aggiornamento profilo:', error);
      toast.error('Errore di connessione');
    }
  };

  // Gestione cambio password
  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/cambia-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      if (response.ok) {
        toast.success('Password cambiata con successo!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setIsChangingPassword(false);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Errore nel cambio password');
      }
    } catch (error) {
      console.error('Errore cambio password:', error);
      toast.error('Errore di connessione');
    }
  };

  const getUserInitials = () => {
    if (profileData.nome && profileData.cognome) {
      return `${profileData.nome.charAt(0)}${profileData.cognome.charAt(0)}`.toUpperCase();
    }
    return profileData.username?.charAt(0).toUpperCase() || 'U';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non disponibile';
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento profilo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header della pagina */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Il Mio Profilo</h1>
        <p className="mt-2 text-gray-600">Gestisci le tue informazioni personali e le impostazioni dell'account</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Sidebar con avatar e info base */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6">
            {/* Avatar */}
            <div className="text-center">
              <div className="mx-auto h-24 w-24 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                {getUserInitials()}
              </div>
              <h2 className="mt-4 text-xl font-semibold text-gray-900">
                {profileData.nome} {profileData.cognome}
              </h2>
              <p className="text-gray-600">@{profileData.username}</p>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-2">
                {user.role === 'admin' ? 'Amministratore' : 'Utente'}
              </span>
            </div>

            {/* Info rapide */}
            <div className="mt-6 space-y-3">
              <div className="flex items-center text-sm text-gray-600">
                <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                {profileData.email || 'Email non specificata'}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                {profileData.telefono || 'Telefono non specificato'}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                Ultimo accesso: {formatDate(user.lastLogin)}
              </div>
            </div>

            {/* Pulsante cambio password */}
            <div className="mt-6">
              <button
                onClick={() => setIsChangingPassword(!isChangingPassword)}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <KeyIcon className="h-4 w-4 mr-2" />
                Cambia Password
              </button>
            </div>
          </div>
        </div>

        {/* Contenuto principale */}
        <div className="lg:col-span-2 space-y-8">
          {/* Sezione Informazioni Personali */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Informazioni Personali</h3>
                {!isEditingProfile && (
                  <button
                    onClick={handleProfileEdit}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Modifica
                  </button>
                )}
              </div>
            </div>

            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <IdentificationIcon className="h-4 w-4 inline mr-1" />
                    Nome
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="text"
                      value={profileData.nome}
                      onChange={(e) => setProfileData({...profileData, nome: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{profileData.nome || 'Non specificato'}</p>
                  )}
                </div>

                {/* Cognome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <IdentificationIcon className="h-4 w-4 inline mr-1" />
                    Cognome
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="text"
                      value={profileData.cognome}
                      onChange={(e) => setProfileData({...profileData, cognome: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{profileData.cognome || 'Non specificato'}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <EnvelopeIcon className="h-4 w-4 inline mr-1" />
                    Email
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{profileData.email || 'Non specificata'}</p>
                  )}
                </div>

                {/* Telefono */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <PhoneIcon className="h-4 w-4 inline mr-1" />
                    Telefono
                  </label>
                  {isEditingProfile ? (
                    <input
                      type="tel"
                      value={profileData.telefono}
                      onChange={(e) => setProfileData({...profileData, telefono: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{profileData.telefono || 'Non specificato'}</p>
                  )}
                </div>

                {/* Username (non modificabile) */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <UserCircleIcon className="h-4 w-4 inline mr-1" />
                    Username
                  </label>
                  <p className="text-gray-900 py-2 bg-gray-50 px-3 rounded-md">
                    {profileData.username}
                    <span className="text-sm text-gray-500 ml-2">(non modificabile)</span>
                  </p>
                </div>
              </div>

              {/* Pulsanti di azione per modifica profilo */}
              {isEditingProfile && (
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={handleProfileCancel}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Annulla
                  </button>
                  <button
                    onClick={handleProfileSave}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <CheckIcon className="h-4 w-4 mr-1" />
                    Salva Modifiche
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sezione Cambio Password */}
          {isChangingPassword && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Cambia Password</h3>
              </div>

              <div className="px-6 py-6">
                <div className="space-y-6">
                  {/* Password attuale */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password Attuale
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Inserisci la password attuale"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Nuova password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nuova Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Inserisci la nuova password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Conferma password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Conferma Nuova Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Conferma la nuova password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Requisiti password */}
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Requisiti Password:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Almeno 6 caratteri</li>
                      <li>• Combinazione di lettere e numeri consigliata</li>
                      <li>• Evita di usare informazioni personali</li>
                    </ul>
                  </div>

                  {/* Pulsanti azione cambio password */}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setIsChangingPassword(false);
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                      }}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <XMarkIcon className="h-4 w-4 mr-1" />
                      Annulla
                    </button>
                    <button
                      onClick={handlePasswordChange}
                      disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <KeyIcon className="h-4 w-4 mr-1" />
                      Cambia Password
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sezione Informazioni Account */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Informazioni Account</h3>
            </div>

            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
                  <p className="text-gray-900">
                    {user.role === 'admin' ? 'Amministratore' : 'Utente'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stato Account</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.active ? 'Attivo' : 'Disattivato'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Creazione</label>
                  <p className="text-gray-900">{formatDate(user.createdAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ultimo Aggiornamento</label>
                  <p className="text-gray-900">{formatDate(user.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfiloPage; 